/**
 * Provides an interface for viewing and modifying properties of a given layer.
 *
 * ## Events
 * Name | Arguments | Description
 * --- | --- | ---
 * layer-model-update | The `MapLayerBase` and the `Layer` associated with the layer, if any. | Fires whenever the layer model is updated successfully.
 * layer-model-remove | The `MapLayerBase` and the `Layer` associated with the layer, if any. | Fires when the user requests to remove the layer.
 * model-select | The `FormResponse` selected. | Fires when a selection occurs on an underlying `ModelDirectoryView`.
 * opacity | The `MapLayerBase` and the new opacity value. | Fires when the opacity value of the layer is changed.
 * @module app/views/map/LayerPanelView
 * @extends {MapDetailView}
 */
import MapDetailView from "app/views/map/MapDetailView";
import _ from "underscore";
import $ from "jquery";
import Field from "core/models/Field";
import FormInputView from "core/views/FormInputView";
import SymbolFactory from "app/models/map/SymbolFactory";
import TabView from "core/views/TabView";
import DetailView from "core/views/DetailView";
import Backbone from "backbone";
import GeographicSummarizer from "app/models/map/summarize/GeographicSummarizer";
import $bootstrap from "bootstrap";
import logger from "app/Logger";
import mapContext from "app/views/map/MapContext";
export default MapDetailView.extend({
  className: "layer-detail-view no-select",

  /**
   * Initializes the view.
   * @param  {object} options The options hash.
   * @param {string} options.title:'Layer Detail' The title for the view.
   * @param {Boolean|Integer} options.opacity `true` to use the opacity value from the given layer, otherwise a number to which to set the opacity, otherwise `false`.
   * @param {MapLayerBase} options.layer The layer to associate with this view.
   * @param {MapContext} options.context The MapContext associated with this view.
   */
  initialize: function (options) {
    MapDetailView.prototype.initialize.apply(this, arguments);

    this.options = _.defaults({}, options, {
      opacity: true,
      title: "Layer Detail",
    });
    this._layer = this.options.layer;
    this.setTitle(this.options.title);

    if (this.options.opacity === true) {
      if (this._layer.layer && _.isNumber(this._layer.layer.opacity)) {
        this.options.opacity = this._layer.layer.opacity * 100;
      } else {
        this.options.opacity = 100;
      }
    }
  },

  events: {
    "slide .slider": function () {
      var value = this._slider.slider("getValue");

      this.setOpacity(value / 100.0);
    },
  },

  template: _.template(
    '<div><%if(opacity !== false){%><label class="control-label">Opacity</label><span class="slider" data-slider-max="100" data-slider-value="<%- opacity%>"></span><%}%><div class="after"></div></div>'
  ),

  render: function () {
    var layer = this._layer;

    var tabs = [
      {
        view: new Backbone.View({
          el: $(this.template(this.options))[0],
        }),
        buttons: [],
        title: "Appearance",
      },
    ];

    var summarizers = [];

    if (
      !layer.serviceLayer ||
      !layer.serviceLayer.service ||
      !layer.serviceLayer.service.get("external")
    ) {
      summarizers.push(new GeographicSummarizer());
    }

    /*_.each(this.options.context.plugins, function (plugin) {
			if (plugin.handlesLayer(layer)) {
				var view = plugin.viewForLayer(layer);
				var summarizer = plugin.getSummarizer();

				if (view) {
					tabs.push({
						view: new Backbone.View({
							el: view[0]
						}),
						buttons: [],
						title: plugin.name
					});
				}

				if (summarizer) {
					summarizers.push(summarizer);
				}
			}
		}, this);*/

    _.each(
      summarizers,
      function (summarizer) {
        var summary = summarizer.summarizeLayer(layer);
        var summaryView = new DetailView({
          model: summary,
          fields: summary.summary_fields,
        });

        summaryView.render();

        tabs.push({
          view: summaryView,
          buttons: [],
          title: summarizer.getName(),
        });
      },
      this
    );

    this.$el.uniqueId();
    this._tabs = new TabView({
      tabs: tabs,
      prefix: this.$el.attr("id"),
    });

    this.listenTo(
      this._tabs,
      "enter",
      function (tab) {
        var buttons = $();

        _.each(tab.buttons, function (buttonView) {
          buttons = buttons.add(buttonView.$el);
        });
        //tab.start();
        //this._setFooter(buttons);
        tab.view.render();
        if (tab.title) {
          logger.operation("tab-clicked", { title: tab.title });
        }
      },
      this
    );
    this._tabs.render();

    this.$el.append(this._tabs.$el);
    this._slider = this.$(".slider").slider();

    this.refreshControls();

    if (
      !(
        this._layer.serviceLayer &&
        this._layer.serviceLayer.service.get("external")
      )
    ) {
      this.setSymbolFactory(this._layer.getSymbolFactory());
    }
  },

  refreshControls: function () {
    this.clearControls();
    this.addControl(
      this.createIconButton(
        mapContext.getOperation("pan-to-item").withModel(this._layer)
      )
    );
    this.addControl(
      this.createIconButton(
        mapContext.getOperation("zoom-to-item").withModel(this._layer)
      )
    );
    this.addControl(
      this.createIconButton(
        mapContext.getOperation("set-selectable").withModel(this._layer)
      )
    );
    this.addControl(
      this.createIconButton(
        mapContext.getOperation("set-unselectable").withModel(this._layer)
      )
    );
    this.addControl(
      this.createIconButton(
        mapContext.getOperation("view-item-files").withModel(this._layer)
      )
    );
    this.addControl(
      this.createIconButton(
        mapContext.getOperation("edit-layer").withModel(this._layer)
      )
    );
    this.addControl(
      this.createIconButton(
        mapContext.getOperation("manage-layer-security").withModel(this._layer)
      )
    );
    this.addControl(
      this.createIconButton(
        mapContext.getOperation("delete-layer").withModel(this._layer)
      )
    );
    this.addControl(
      this.createIconButton(
        mapContext.getOperation("download-item").withModel(this._layer)
      )
    );
  },

  /**
   * Sets the opacity of the underlying layer.
   * @param {Number} opacity A number between 0 and 1, inclusive, to which to set the opacity.
   */
  setOpacity: function (opacity) {
    this._layer.layer.setOpacity(opacity);
    this._slider.slider("setValue", opacity * 100);
    this.trigger("opacity", this._layer, opacity);
  },

  /**
   * Sets the symbol factory to use for the layer.
   * @param {SymbolFactory} symbolFactory The symbol factory.
   */
  setSymbolFactory: function (symbolFactory) {
    var layer = this._layer;
    var self = this;

    var inputViews = _.map(_.values(SymbolFactory.fields), function (
      fieldOptions
    ) {
      var field = new Field(fieldOptions);

      if (field.get("name") == "linkColors") {
        var SELECTED_COLOR = "rgb(0, 0, 0)",
          UNSELECTED_COLOR = "rgb(211, 211, 211)";

        field = field.decorate({
          editor: "custom",
          label: "",
          EditorView: {
            tagName: "span",
            className: "glyphicon glyphicon-link",

            template: function () {
              return "";
            },

            events: {
              click: function () {
                this.setValue(!this.getControlValue());
                this.onChange();
              },
            },

            postRender: function () {
              this.$el.css({
                display: "block",
                "content-align": "center",
                "text-align": "center",
              });

              this.setControlValue(this.model.get("linkColors"));

              $bootstrap(this.$el).tooltip({
                title: "Link the fill color and border colors",
              });
            },

            getControlValue: function () {
              return this.$el.data("value");
            },

            setControlValue: function (value) {
              this.$el.data("value", value);

              if (value) {
                this.$el.css("color", SELECTED_COLOR);
              } else {
                this.$el.css("color", UNSELECTED_COLOR);
              }
            },
          },
        });
      }

      var inputView = new FormInputView({
        field: field,
        model: symbolFactory,
      });

      inputView.render();

      return inputView;
    });
    var after = this.$(".after");

    after.children().remove();
    _.each(
      inputViews,
      _.bind(function (inputView) {
        after.append(inputView.$el);
      }, this)
    );
    layer.setSymbolFactory(symbolFactory);
  },
});



//////////////////
// WEBPACK FOOTER
// ./public/js/app/views/map/LayerPanelView.js
// module id = 948
// module chunks = 1